import type { ComponentPropsOptions, PropType, Ref } from "vue";
import type { GlobalOptionsSelect, OptionSelect, OptionValue } from "@/components/helpers/types";
import { useVModel } from "@vueuse/core";
import Fuse from "fuse.js";
import { debounce } from "lodash-es";
import { match } from "ts-pattern";
import { computed, defineComponent, ref, useId, watch } from "vue";
import { toOptionSelect } from "@/components/helpers/types";
import Spinner from "@/components/Spinner";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { PromiseQueue } from "@/utils/queues";
import { renderComponent } from "@/utils/render-component";
import Popover from "../../primitives/popover/Popover";
import { selectTriggerVariants } from "../../primitives/select/variants";
import Badge from "../badge/Badge";
import Command from "../command/Command";
import { fieldCtx } from "../field/Field";
import Icon from "../icon/Icon";

export type FetchOptions = (query: string) => Promise<OptionSelect[]>;
type ComboboxValue = string | number | null;

export const useRemote = (
  search: FetchOptions | GlobalOptionsSelect[] | undefined,
  open: Ref<boolean>
) => {
  const searchTerm = ref("");
  const searchOptions = ref<OptionSelect[]>([]);
  const loading = ref(false);

  if (typeof search === "function") {
    const cache = ref<Map<string, OptionSelect[]>>(new Map());
    const queue = new PromiseQueue();

    async function searchFn(term: string) {
      if (typeof search !== "function") return;

      loading.value = true;
      const results = await search(term).finally(() => {
        loading.value = false;
      });
      searchOptions.value = results;
      cache.value.set(term, results);
    }

    searchFn(searchTerm.value);

    watch(
      [searchTerm, open],
      debounce(async ([term]) => {
        if (typeof search !== "function") return;
        if (!open.value) return;
        if (!search) return;

        if (cache.value.has(term)) {
          searchOptions.value = cache.value.get(term) as OptionSelect[];
          return;
        }

        queue.add(() => searchFn(term));
      }, 300),
      { immediate: true }
    );
  }

  return {
    searchTerm,
    searchOptions,
    loading,
  };
};

const comboboxProps = {
  modelValue: p.custom<ComboboxValue | ComboboxValue[]>([String, Array, Number]),
  options: {
    type: [Array, Function] as PropType<GlobalOptionsSelect[] | FetchOptions>,
    required: true,
  },
  multiple: p.boolean(),
  id: p.string(),
  required: p.boolean(),
  name: p.string(),
  size: p.string<"sm" | "md" | "lg">("md"),
  placeholder: p.string(),
  disabled: p.boolean(),
  onChange: p.func(),
  maxTags: p.number(3),
  searchType: p.string<"fuzzy" | "exact">("fuzzy"),
  fullWidth: p.boolean(true),
  truncate: p.boolean(false),
  clearable: p.boolean(false),
  clearLabel: p.string(),
} satisfies ComponentPropsOptions;

export default defineComponent({
  name: "Combobox",
  emits: {
    "update:modelValue": (_value: ComboboxValue | ComboboxValue[]) => true,
  },
  props: comboboxProps,
  setup(props) {
    const field = fieldCtx.inject(props);
    const isRemote = typeof props.options === "function";
    const selected = useVModel(props, "modelValue");

    // make sure type is correct
    if (!props.multiple && Array.isArray(selected.value)) {
      selected.value = "";
    }

    if (props.multiple && !Array.isArray(selected.value)) {
      selected.value = [];
    }

    // this is used to keep track of the selected values in remote mode
    const intermediateValue = ref<OptionSelect[]>([]);
    watch(
      intermediateValue,
      (val) => {
        selected.value = val.map((i) => i.value);
      },
      { immediate: props.multiple }
    );

    const open = ref(false);
    const { loading, searchOptions, searchTerm } = useRemote(props.options, open);

    const options = computed<OptionSelect[]>(() => {
      if (typeof props.options === "function") {
        return searchOptions.value.map(toOptionSelect);
      }

      return props.options?.map(toOptionSelect) ?? [];
    });

    // AI-196: Sync selected → intermediateValue when options load with pre-populated values
    // This fixes the issue where EditMember opens with pre-selected IDs but intermediateValue is empty
    watch(
      [options, () => selected.value],
      ([opts, selectedVals]) => {
        if (
          props.multiple &&
          Array.isArray(selectedVals) &&
          selectedVals.length > 0 &&
          intermediateValue.value.length === 0 &&
          opts.length > 0
        ) {
          intermediateValue.value = selectedVals
            .map((val) => opts.find((opt) => opt.value === val))
            .filter((opt): opt is OptionSelect => opt !== undefined);
        }
      },
      { immediate: true }
    );

    const placeholder = (
      <span class="text-muted-foreground">{props.placeholder || "Selecione..."}</span>
    );

    function getLabel(value: OptionValue, ctx: "select" | "button") {
      const ctxOptions = isRemote && props.multiple ? intermediateValue.value : options.value;

      const opt = ctxOptions.find((opt) => opt.value === value);

      if (opt?.btnLabel && ctx === "button") {
        return renderComponent(opt.btnLabel);
      }

      return opt?.label ? renderComponent(opt.label) : placeholder;
    }

    function onSelect(value: GlobalOptionsSelect) {
      if (!props.multiple && !Array.isArray(selected.value)) {
        const val = typeof value === "string" ? value : value.value;

        selected.value = val;
        open.value = false;
        return;
      }

      if (props.multiple && Array.isArray(selected.value)) {
        let val: OptionSelect;

        // handle option
        if (typeof value === "string") {
          val = options.value.find((opt) => opt.value === value) as OptionSelect;
        } else {
          val = value;
        }

        if (!val) return;

        if (isOptionSelected(val.value)) {
          removeOption(val.value);
          return;
        }

        addOption(val.value);
      }
    }

    function removeOption(value: OptionValue) {
      if (!props.multiple || !Array.isArray(selected.value)) return;

      if (isRemote) {
        intermediateValue.value = intermediateValue.value.filter((i) => i.value !== value);
      } else {
        selected.value = selected.value.filter((i) => i !== value);
      }
    }

    function addOption(value: OptionValue) {
      if (!props.multiple || !Array.isArray(selected.value)) return;

      if (isRemote) {
        const opt = options.value.find((i) => i.value === value);
        if (!opt) return;
        intermediateValue.value = [...intermediateValue.value, opt];
      } else {
        selected.value = [...selected.value, value];
      }
    }

    function removeLastOption() {
      if (!props.multiple || !Array.isArray(selected.value)) return;

      if (isRemote) {
        intermediateValue.value = intermediateValue.value.slice(
          0,
          intermediateValue.value.length - 1
        );
      } else {
        selected.value = selected.value.slice(0, selected.value.length - 1);
      }
    }

    function isOptionSelected(value: OptionValue): boolean {
      if (!props.multiple) return false;
      if (!Array.isArray(selected.value)) return false;

      return selected.value?.includes(value);
    }

    function handleTriggerKeyboard(e: KeyboardEvent) {
      if (!open.value) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          open.value = true;
          e.preventDefault();
        }
      }
    }

    function handleButtonKeyboard(e: KeyboardEvent) {
      if (!props.multiple || !Array.isArray(selected.value)) return;
      if (!selected.value.length) return;

      if (e.key === "Backspace" && !searchTerm.value) {
        removeLastOption();
      }
    }

    function updateCollision() {
      globalThis.dispatchEvent(new Event("resize"));
    }

    const listboxId = useId();
    const fieldDescriptionId = computed(() => {
      if (field.errorMessage) return `${field.id}-error`;
      if (field.hint) return `${field.id}-hint`;
      return undefined;
    });

    return () => (
      <Popover.Root v-model:open={open.value}>
        <Popover.Trigger
          class="group"
          asChild
          //@ts-expect-error
          onKeydown={handleTriggerKeyboard}
        >
          <button
            id={field.id}
            disabled={props.disabled}
            class={cn(
              selectTriggerVariants({ size: field.size }),
              "flex w-full justify-between gap-2 text-left"
            )}
            role="combobox"
            aria-expanded={open.value}
            aria-controls={listboxId}
            aria-describedby={fieldDescriptionId.value}
            aria-invalid={!!field.errorMessage}
            aria-required={field.required}
            onKeydown={handleButtonKeyboard}
          >
            {match(props.multiple)
              .with(false, () => {
                return (
                  <span class={cn(props.truncate && "truncate")}>
                    {getLabel(selected.value as OptionValue, "button")}
                  </span>
                );
              })
              .with(true, () => {
                const values = selected.value as OptionValue[];
                if (!values?.length) return placeholder;

                return (
                  <span class="-mx-2 flex flex-wrap gap-1">
                    {values.slice(0, props.maxTags).map((value) => (
                      <div class="flex items-center justify-center gap-1 rounded border-accent-6 bg-accent-3A px-2 py-0.5 text-xs text-accent-11 aria-current:bg-accent-4">
                        {getLabel(value as string, "button")}

                        <button
                          class="default-focus -mr-1 rounded-sm p-px hover:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeOption(value);
                          }}
                          aria-label={$t("remove_tag")}
                        >
                          <Icon
                            icon="lucide:x"
                            class="h-4 w-4 rounded p-0.5 hover:bg-foreground/10"
                          />
                        </button>
                      </div>
                    ))}
                    {values.length > props.maxTags && (
                      <Badge class="bg-accent-3A text-accent-11" size="xs">
                        {values.length - 3}+
                      </Badge>
                    )}
                  </span>
                );
              })
              .otherwise(() => (
                <div></div>
              ))}
            <div class="center flex gap-1">
              <button
                v-show={
                  props.clearable &&
                  (props.multiple ? (selected.value as any[]).length : selected.value)
                }
                class="opacity-0 group-hover:opacity-70"
                onClick={(e) => {
                  e.stopPropagation();
                  selected.value = props.multiple ? [] : null;
                }}
              >
                <Icon icon="lucide:x" class="ml-auto h-4 w-4 flex-none opacity-70" />
              </button>
              <Icon icon="lucide:chevron-down" class="ml-auto h-4 w-4 flex-none opacity-70" />
            </div>
          </button>
        </Popover.Trigger>
        <Popover.Content
          onOpenAutoFocus={updateCollision}
          avoidCollisions
          prioritizePosition
          updatePositionStrategy="always"
          class="z-80 overflow-hidden p-0"
          fullWidth={props.fullWidth}
          align="start"
        >
          <Command.Root
            multiple={props.multiple}
          >
            <div class="relative w-full" v-show={options.value.length}>
              <Command.Input autoFocus v-model={searchTerm.value}></Command.Input>

              {loading.value && <Spinner class="absolute right-3 top-3 h-4 w-4" />}
            </div>

            <Command.List
              onEscapeKeyDown={() => {
                open.value = false;
              }}
              v-slots={{
                default: () => (
                  <>
                    <Command.Empty class="p-5">{$t("table.no_results_found")}</Command.Empty>
                    <Command.Group>
                      {options.value.length ? (
                        options.value
                          .slice()
                          .sort(
                            // put the select item on top
                            (a, b) => {
                              if (a.value === selected.value) return -1;
                              if (b.value === selected.value) return 1;
                              return 0;
                            }
                          )
                          .map((item) => {
                            return (
                              <Command.Item
                                disabled={item.disabled}
                                onSelect={onSelect}
                                key={String(item.value)}
                                value={item}
                                textValue={item.textValue}
                                class="overflow-visible!"
                              >
                                {props.multiple ? (
                                  <>
                                    <div
                                      class={cn(
                                        "mr-2 flex h-4 w-4 items-center justify-center rounded border p-px",
                                        isOptionSelected(item.value)
                                          ? "border-brand-9 bg-brand-9 text-background"
                                          : "opacity-80 [&_svg]:invisible"
                                      )}
                                    >
                                      <Icon icon="lucide:check" class="h-3 w-3" />
                                    </div>
                                    {renderComponent(item.label)}
                                  </>
                                ) : (
                                  <div class="flex w-full items-center">
                                    <div class="mr-2 w-6 flex-none">
                                      <Icon
                                        v-show={item.value === selected.value}
                                        icon="lucide:check"
                                        class="h-4 w-4"
                                      />
                                    </div>
                                    <div class="flex-1">{renderComponent(item.label)}</div>
                                  </div>
                                )}
                              </Command.Item>
                            );
                          })
                      ) : (
                        <div class="center p-3 text-center text-muted-foreground">
                          Sem opções disponíveis
                        </div>
                      )}
                    </Command.Group>
                  </>
                ),
                footer: () =>
                  props.clearable &&
                  (props.multiple ? (selected.value as any[]).length : selected.value) && (
                    <Command.Footer>
                      <Command.Item
                        value={" "}
                        class="justify-center py-1 text-center"
                        onSelect={() => {
                          selected.value = props.multiple ? [] : null;
                          open.value = false;
                        }}
                      >
                        {props.clearLabel || $t("clear_selection")}
                      </Command.Item>
                    </Command.Footer>
                  ),
              }}
            />
          </Command.Root>
        </Popover.Content>
      </Popover.Root>
    );
  },
});

export function fuzzySearch(list: any[], term: string) {
  if (!term) return list;
  const fuse = new Fuse(list, {
    keys: ["label", "textValue"],
    threshold: 0.3,
  });

  return fuse.search(term).map((i) => i.item);
}
